import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';

import { AbsenceBadge } from '@/components/AbsenceBadge';
import { Modal } from '@/components/Modal';
import { leadBadge, mutedText } from '@/components/personStyles';
import { ZOOM_INITIAL_SIZE, ZoomablePhoto } from '@/components/ZoomablePhoto';
import {
  useDirectoryAssignedDuties,
  useDirectoryPerson,
  type DirectoryAssignedDuty,
  type DirectoryPerson,
  type DirectoryScheduleItem,
} from '@/lib/directory/useDirectory';
import { matchesDuties } from '@/lib/text/dutySearch';
import { mailtoHref, safeHref } from '@/lib/utils/url';
import { SeatMapModal } from '@/pages/hr/SeatMapModal';

/**
 * 직원 한 명의 <b>기본 정보 카드</b>. 직원찾기 모달과 인력배치표가 같은 것을 쓴다.
 *
 * <h2>여기 있는 것이 "누구나 볼 수 있는 것" 의 정의다</h2>
 * 연락처 · 소속(현장이면 현장명) · 오늘 상태(휴가·출장·회의). 그 이상은 없다 —
 * 생년월일 · 입사일 · 기술등급 · 경력 · 급여 · 고과는 <b>응답에 담기지도 않는다</b>({@code DirectoryController}).
 * 화면에서 감추는 것이 아니라 서버가 보내지 않는 것이라, 이 파일을 어떻게 고쳐도 새지 않는다.
 *
 * <p>인사팀에게는 <b>경력관리로 가는 링크</b> 하나만 더 보인다. 민감 항목을 카드에 끌어오지 않은
 * 이유가 그것이다 — 한 응답을 권한에 따라 갈라 내려보내기 시작하면, 이 카드를 읽는 모든 화면이
 * "지금 누가 보고 있는가" 를 함께 신경 써야 한다.
 */
/**
 * collapseMatrixDuties — 직원찾기 명함은 「담당 업무」 를 담당업무 줄 끝 버튼 뒤의 모달로 보낸다(2026-10-14 지시).
 * 조직도·배치표에서 여는 명함은 그대로 보인다. 2026-10-30 부터 그 업무는 전결표가 아니라 업무분장(V599)이다
 * (「전결표 업무 대신 담당 업무가 보이게」).
 */
export function PersonCard({
  person,
  collapseMatrixDuties = false,
  matrixKeyword = '',
}: {
  person: DirectoryPerson;
  collapseMatrixDuties?: boolean;
  /** 직원찾기 검색어 — 업무분장 업무에 걸리면 버튼과 모달의 그 줄을 칠한다. */
  matrixKeyword?: string;
}) {
  const subtitle = [person.rank, person.position, person.field].filter(Boolean).join(' · ');

  /**
   * 본사 사람인가 — 자리배치도에서 찾아 줄 수 있는 대상. 부서 소속이면 본사로 본다.
   * 현장 인원에게는 자리가 없고, 그 사람이 어디 있는지는 「소속」의 현장명이 이미 답한다.
   *
   * <p>자리배치도는 이제 PDF 가 아니라 판이다(V392 · {@code SeatMapView}) — 자리가 아직 없는 사람도
   * 판에서는 「자리 없음」 목록에 서므로, 본사 사람이면 늘 연다.
   */
  const atHeadOffice = person.units.some((unit) => unit.unitType === 'DEPARTMENT');
  const [floorplanOpen, setFloorplanOpen] = useState(false);

  return (
    <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
      <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
        {/*
          76×96 으로는 얼굴이 식별되지 않는다 — 누르면 크게 띄운다(2026-09-28). 사진이 없는 사람도
          같은 창을 열고 이니셜을 크게 세운다: 누를 수 있는 자리가 사람마다 다르면 그것이 더 헷갈린다.
        */}
        <ZoomablePhoto
          src={person.photoUrl}
          name={person.name}
          style={{ width: '76px', height: '96px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0, border: '1px solid var(--border)' }}
          initial={<Avatar name={person.name} size={76} />}
          initialLarge={<Avatar name={person.name} size={ZOOM_INITIAL_SIZE} />}
        />

        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>{person.name}</span>
            <StatusBadge status={person.today.status} label={person.today.statusLabel} />
            <AbsenceBadge personnelId={person.id} />
          </div>
          {subtitle && (
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '3px' }}>{subtitle}</p>
          )}
          {person.deptSite && (
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{person.deptSite}</p>
          )}
        </div>

        {/* 사진 줄의 맨 오른쪽. `marginLeft: auto` 로 민다 — 가운데 칸이 이름 길이만큼만 차지하므로
            그 뒤 빈 자리를 이 버튼이 전부 먹으면 이름 옆에 붙어 버린다. */}
        {atHeadOffice && (
          <button
            type="button"
            className="btn-secondary"
            style={{ fontSize: '12px', padding: '4px 10px', marginLeft: 'auto', flexShrink: 0 }}
            onClick={() => setFloorplanOpen(true)}
          >
            자리배치도 보기
          </button>
        )}
      </div>

      <Section title="연락처">
        {/* 링크로 둔다 — 사내 주소록을 여는 대부분의 이유가 "지금 연락하려고" 다. */}
        <Row label="전화" value={person.phone} href={person.phone ? `tel:${person.phone}` : null} />
        <Row label="내선" value={person.extension} />
        <Row label="이메일" value={person.email} href={person.email ? mailtoHref(person.email, person.name) : null} />
        {/* 담당업무(V437) — 연락처 줄 아래에 같은 모양으로(사용자 지시 2026-10-14). 여러 줄 글은 줄바꿈대로. */}
        {/* 업무분장(V599)에서 이 사람이 담당자인 업무 — 담당업무 글에 써 넣지 않고 따로 싣는다(2026-10-14 지시 ·
            2026-10-30 전결표 업무 → 업무분장). 직원찾기는 건수가 많아 담당업무 줄 끝의 버튼으로 모달에서 본다. */}
        <Row
          label="담당업무"
          value={person.duties ?? null}
          trailing={
            collapseMatrixDuties ? (
              <AssignedDutiesButton
                key={person.id}
                personnelId={person.id}
                name={person.name}
                count={person.assignedDuties?.length ?? 0}
                keyword={(person.assignedDuties ?? []).some((d) => matchesDuties(d, matrixKeyword)) ? matrixKeyword : ''}
              />
            ) : undefined
          }
        />
        {!collapseMatrixDuties && (
          <Row label="업무분장" value={person.assignedDuties?.length ? person.assignedDuties.join(', ') : null} />
        )}
        {/* 학력(V615) — 본인·인사팀이 공개로 둔 사람만 온다. 비공개면 줄이 서지 않는다. */}
        {(person.educations?.length ?? 0) > 0 && <Row label="학력" value={(person.educations ?? []).join(' / ')} />}
      </Section>

      <Section title="소속">
        {person.units.length === 0 ? (
          <p style={mutedText}>등록된 소속이 없습니다.</p>
        ) : (
          person.units.map((unit) => (
            <div key={`${unit.unitType}:${unit.unitName}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 0' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                {unit.unitType === 'SITE' ? '현장' : '부서'}
              </span>
              {/* 부서 소속이면 층까지 — "견적팀 - 대리 - 3층". 현장 소속은 현장명이 이미 답한다. */}
              <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                {[unit.unitName, unit.roleTitle || person.rank, unit.unitType === 'SITE' ? null : person.floor]
                  .filter(Boolean)
                  .join(' - ')}
              </span>
              {unit.teamLead && (
                <span style={leadBadge}>
                  {unit.unitType !== 'SITE' ? '팀장' : unit.roleTitle === '현장소장' ? '현장소장' : '현장대리인'}
                </span>
              )}
            </div>
          ))
        )}
      </Section>

      <Section title={`오늘 일정 (${person.today.date})`}>
        {person.today.items.length === 0 ? (
          <p style={mutedText}>등록된 휴가·출장·회의가 없습니다.</p>
        ) : (
          person.today.items.map((item, index) => <ScheduleRow key={index} item={item} />)
        )}
      </Section>


      {floorplanOpen && (
        <SeatMapModal personnelId={person.id} name={person.name} onClose={() => setFloorplanOpen(false)} />
      )}
    </div>
  );
}

/**
 * 카드를 그대로 모달로 띄운다. 인력배치표처럼 <b>목록만 있는 화면</b>에서 사람을 눌렀을 때 쓴다 —
 * 그 화면들이 각자 명함을 다시 그리면 "누구나 볼 수 있는 것" 의 정의가 화면 수만큼 생긴다.
 */
export function PersonCardModal({ personnelId, onClose }: { personnelId: string; onClose: () => void }) {
  const person = useDirectoryPerson(personnelId);

  return (
    <Modal title="직원 정보" onClose={onClose} maxWidth="520px">
      {person.isPending ? (
        <p style={{ ...mutedText, padding: '80px 0', textAlign: 'center' }}>불러오는 중…</p>
      ) : person.isError || !person.data ? (
        <p style={{ ...mutedText, padding: '80px 0', textAlign: 'center' }}>직원 정보를 불러오지 못했습니다.</p>
      ) : (
        <PersonCard person={person.data} />
      )}
    </Modal>
  );
}

function ScheduleRow({ item }: { item: DirectoryScheduleItem }) {
  // 모르는 종류는 회의 색으로 떨어뜨린다 — 색이 없어 칸이 통째로 비는 것보다 낫다.
  const tone = SCHEDULE_TONE[item.kind] ?? MEETING_TONE;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 10px',
        marginBottom: '4px',
        borderRadius: '6px',
        background: tone.background,
        borderLeft: `3px solid ${tone.accent}`,
      }}
    >
      <span style={{ fontSize: '11px', fontWeight: 600, color: tone.text, flexShrink: 0 }}>{tone.label}</span>
      {/* 시간이 없는 칸도 자리를 잡아 둔다 — 없으면 아래 줄의 제목이 들쭉날쭉해진다. */}
      <span style={{ fontSize: '12px', color: 'var(--text-muted)', minWidth: '38px', flexShrink: 0 }}>
        {item.time ?? '종일'}
      </span>
      <span className="truncate" style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{item.title}</span>
      {item.detail && (
        <span className="truncate" style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{item.detail}</span>
      )}
    </div>
  );
}

type ScheduleTone = { label: string; background: string; accent: string; text: string };

/**
 * 기본값을 **표 밖에 따로 둔다.** 표에서 꺼낸 값은 `Record` 조회라 항상 `undefined` 가 섞이고
 * (`noUncheckedIndexedAccess`), `?? SCHEDULE_TONE.meeting` 처럼 같은 표에서 되받으면 그 조회도
 * 마찬가지라 결국 undefined 가 남는다. 이름 붙은 상수 하나면 그 고리가 끊긴다.
 */
const MEETING_TONE: ScheduleTone = {
  label: '회의',
  background: 'var(--accent-bg)',
  accent: 'var(--accent)',
  text: '#1d4ed8',
};

/** 휴가(초록) · 부재(주황) · 회의(파랑). 색으로 갈라야 목록을 훑을 때 종류가 먼저 읽힌다. */
const SCHEDULE_TONE: Record<string, ScheduleTone> = {
  leave: { label: '휴가', background: '#dcfce7', accent: '#22c55e', text: '#15803d' },
  absence: { label: '부재', background: '#ffedd5', accent: '#f97316', text: '#c2410c' },
  meeting: MEETING_TONE,
};

function StatusBadge({ status, label }: { status: string; label: string }) {
  const tone =
    status === 'LEAVE'
      ? { background: '#dcfce7', color: '#15803d' }
      : status === 'ABSENCE'
        ? { background: '#ffedd5', color: '#c2410c' }
        : status === 'MEETING'
          ? { background: 'var(--accent-bg)', color: '#1d4ed8' }
          : { background: '#f1f5f9', color: '#64748b' };

  return (
    <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', ...tone }}>
      {label}
    </span>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginTop: '16px' }}>
      <p style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px' }}>{title}</p>
      {children}
    </div>
  );
}

function Row({
  label,
  value,
  href,
  trailing,
}: {
  label: string;
  value: string | null;
  href?: string | null;
  /** 줄 오른쪽 끝에 붙는 것(담당업무 줄의 「담당 업무」 버튼). */
  trailing?: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', gap: '8px', padding: '3px 0', alignItems: 'flex-start' }}>
      <span style={{ fontSize: '12px', color: 'var(--text-muted)', minWidth: '48px', flexShrink: 0 }}>{label}</span>
      {value && href ? (
        <a href={safeHref(href)} style={{ fontSize: '13px', color: 'var(--accent)' }}>{value}</a>
      ) : (
        <span style={{ fontSize: '13px', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{value || '-'}</span>
      )}
      {trailing && <span style={{ marginLeft: 'auto', flexShrink: 0 }}>{trailing}</span>}
    </div>
  );
}

/**
 * 담당 업무(업무분장 · V599) — 버튼에는 건수만, 누르면 모달에서 「부서 › 업무구분」 별 표로 주기·범위와 함께.
 * 건수가 많아 제목 줄의 찾기 칸으로 좁힌다. 0건이어도 버튼은 선다.
 */
function AssignedDutiesButton({
  personnelId,
  name,
  count,
  keyword,
}: {
  personnelId: string;
  name: string;
  count: number;
  /** 직원찾기 검색어 중 이 사람의 업무분장 업무에 걸린 것. 비면 칠하지 않는다. */
  keyword: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const duties = useDirectoryAssignedDuties(personnelId, open);
  const firstHit = useRef<HTMLTableRowElement | null>(null);

  // 열자마자 검색어에 걸린 첫 줄을 보이는 자리로 — 아래쪽에 있으면 칠해 둬도 못 본다.
  useEffect(() => {
    firstHit.current?.scrollIntoView({ block: 'center' });
  }, [duties.data]);

  const needle = query.replace(/\s+/g, '').toLowerCase();
  const rows = (duties.data ?? []).filter(
    (d) =>
      needle === '' ||
      [d.deptName, d.workCategory, d.taskDetail, d.roleScope].some((t) => t?.replace(/\s+/g, '').toLowerCase().includes(needle)),
  );
  const firstHitRow = rows.find((row) => matchesDuties(assignedTaskOf(row), keyword));

  return (
    <>
      <button
        type="button"
        className="btn-secondary"
        style={{
          fontSize: '12px',
          padding: '2px 8px',
          whiteSpace: 'nowrap',
          ...(keyword
            ? { background: 'var(--accent-bg)', borderColor: 'var(--accent)', color: 'var(--accent)', fontWeight: 600 }
            : null),
        }}
        onClick={() => setOpen(true)}
      >
        업무분장 {count}
      </button>
      {open && (
        <Modal
          title={`업무분장 · ${name}`}
          onClose={() => setOpen(false)}
          maxWidth="760px"
          actions={
            <input
              className="input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="업무 찾기"
              style={{ width: '180px', fontSize: '13px' }}
            />
          }
        >
          {/* 못 받아 온 사유는 화면에 쓰지 않는다 — 관리자 화면의 몫이다. */}
          {duties.isPending || duties.isError ? (
            <p style={{ ...mutedText, padding: '40px 0', textAlign: 'center' }}>불러오는 중입니다.</p>
          ) : rows.length === 0 ? (
            <p style={{ ...mutedText, padding: '40px 0', textAlign: 'center' }}>
              {needle ? '찾는 업무가 없습니다.' : '이어진 업무가 없습니다.'}
            </p>
          ) : (
            <AssignedDutiesTable rows={rows} keyword={keyword} firstHitRow={firstHitRow} firstHit={firstHit} />
          )}
        </Modal>
      )}
    </>
  );
}

/** 업무분장 한 줄의 업무 이름 — 세부 담당업무, 비었으면 업무구분. 서버의 명함 목록과 같은 규칙이다. */
function assignedTaskOf(row: DirectoryAssignedDuty): string {
  return row.taskDetail ?? row.workCategory ?? '';
}

/** 업무분장 줄들을 「부서 › 업무구분」 묶음의 표로 — 명함 모달과 마이페이지가 같은 모양을 쓴다. */
export function AssignedDutiesTable({
  rows,
  keyword = '',
  firstHitRow,
  firstHit,
}: {
  rows: DirectoryAssignedDuty[];
  keyword?: string;
  firstHitRow?: DirectoryAssignedDuty;
  firstHit?: RefObject<HTMLTableRowElement | null>;
}) {
  const groups: { title: string; rows: DirectoryAssignedDuty[] }[] = [];
  for (const row of rows) {
    const title = [row.deptName, row.workCategory].filter(Boolean).join(' › ') || '-';
    const last = groups[groups.length - 1];
    if (last && last.title === title) last.rows.push(row);
    else groups.push({ title, rows: [row] });
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
      <thead>
        <tr>
          <th style={matrixHead}>세부 담당업무</th>
          <th style={{ ...matrixHead, width: '130px' }}>수행주기·시점</th>
          <th style={{ ...matrixHead, width: '200px' }}>담당현장·배정범위</th>
        </tr>
      </thead>
      {groups.map((group, gi) => (
        <tbody key={`${gi}:${group.title}`}>
          <tr>
            <td colSpan={3} style={{ ...matrixCell, background: 'var(--bg-hover)', fontWeight: 600, color: 'var(--text-secondary)' }}>
              {group.title}
            </td>
          </tr>
          {group.rows.map((row, ri) => {
            const task = assignedTaskOf(row);
            const hit = matchesDuties(task, keyword);
            return (
              <tr
                key={`${ri}:${task}`}
                ref={row === firstHitRow ? firstHit : undefined}
                style={hit ? { background: 'var(--accent-bg)', boxShadow: 'inset 3px 0 0 var(--accent)' } : undefined}
              >
                <td style={matrixCell}>
                  {task}
                  {row.roleScope && <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{row.roleScope}</div>}
                </td>
                <td style={matrixMarkCell}>{row.cycle ?? '-'}</td>
                <td style={{ ...matrixCell, color: 'var(--text-secondary)' }}>{row.siteScope ?? '-'}</td>
              </tr>
            );
          })}
        </tbody>
      ))}
    </table>
  );
}

const matrixHead: CSSProperties = {
  position: 'sticky',
  top: 0,
  background: 'var(--bg-card)',
  borderBottom: '1px solid var(--border-strong)',
  padding: '6px 8px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--text-muted)',
};
const matrixCell: CSSProperties = { borderBottom: '1px solid var(--border)', padding: '6px 8px', verticalAlign: 'top' };
const matrixMarkCell: CSSProperties = { ...matrixCell, whiteSpace: 'nowrap', color: 'var(--text-secondary)' };

/**
 * 목록의 얼굴 칸. 사진이 있으면 <b>사진</b>을, 없으면 이름 첫 자를 그린다.
 *
 * <h2>사진이 늦게 와도 자리는 먼저 잡혀 있다 (사용자 지시 2026-09-29)</h2>
 * 조직도 트리는 사진 없이 먼저 그려지고({@code hasPhoto} 만 받는다), 그림은 브라우저가
 * {@code loading="lazy"} 로 <b>보이는 줄만</b> 뒤늦게 받아 온다. 그래서 칸의 크기·모양을
 * 사진과 이니셜이 <b>똑같이</b> 쓴다 — 다르면 사진이 도착할 때마다 줄이 덜컥거린다.
 *
 * <p>못 받아 온 사진은 이니셜로 물러선다. 저장소에서 파일이 사라졌거나 세션이 끊긴 경우인데,
 * 그때 깨진 이미지 상자를 남기면 이 사람에게만 화면이 고장난 것처럼 보인다.
 *
 * <p>색은 이름에서 뽑아 <b>사람마다 늘 같은 색</b>이 되게 한다 — 목록을 다시 열 때마다 색이
 * 바뀌면 눈으로 찾는 데 방해가 된다.
 */
export function Avatar({ name, size, photoUrl = null }: { name: string; size: number; photoUrl?: string | null }) {
  const palette = ['#e0e7ff', '#dcfce7', '#fef3c7', '#fce7f3', '#e0f2fe', '#f3e8ff'];
  const textPalette = ['#4338ca', '#15803d', '#b45309', '#be185d', '#0369a1', '#7c3aed'];
  let hash = 0;
  for (const char of name) hash = (hash + char.charCodeAt(0)) % palette.length;

  // 사진을 못 받아 온 사람. 주소가 바뀌면(사진을 새로 올렸다) 다시 시도해야 하므로 주소째 기억한다.
  const [failed, setFailed] = useState<string | null>(null);

  const box = {
    width: `${size}px`,
    height: size > 40 ? `${Math.round(size * 1.26)}px` : `${size}px`,
    borderRadius: size > 40 ? '8px' : '50%',
    flexShrink: 0,
  } as const;

  if (photoUrl !== null && failed !== photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        onError={() => setFailed(photoUrl)}
        style={{ ...box, objectFit: 'cover', background: palette[hash], display: 'block' }}
      />
    );
  }

  return (
    <span
      aria-hidden
      style={{
        ...box,
        background: palette[hash],
        color: textPalette[hash],
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `${Math.max(11, Math.round(size * 0.42))}px`,
        fontWeight: 700,
      }}
    >
      {name.slice(0, 1)}
    </span>
  );
}
